"use client";

import type { ReactNode } from "react";
import type {
  ItinerarySuccess,
  RouteCardSummary,
} from "@/lib/planner/api-types";
import type { DayStyle } from "@/lib/planner/day-styles";
import type { AreaSport } from "@/lib/planner/graph-types";
import type { RouteExplanation } from "@/lib/planner/route-explanation";
import type {
  BestRunOption,
  StyleAlternative,
} from "@/lib/planner/route-recommendations";
import type { PlannerObjectives, PlannerPreferences } from "@/lib/planner/preferences";
import { formatHm } from "@/lib/planner/format-time";
import { RouteTrustSummary } from "./RouteTrustSummary";

type RouteRecommendationPanelProps = {
  plan?: ItinerarySuccess;
  explanation: RouteExplanation;
  bestRunsToday: BestRunOption[];
  routeB?: ItinerarySuccess["routeB"];
  styleAlternatives: StyleAlternative[];
  sport: AreaSport;
  selectedRoute?: "A" | "B";
  pendingStyleKey: DayStyle | null;
  onSwapRouteB: () => void;
  onPlanStyle: (alternative: StyleAlternative) => void;
};

export const STYLE_PLAN_ACTION_LABEL = "Plan it";

export function routeBSwapLabel(sport: AreaSport): string {
  return sport === "mtb" ? "Ride Route B instead" : "Ski Route B instead";
}

export function swapToRouteB(plan: ItinerarySuccess): ItinerarySuccess {
  if (!plan.routeB?.snapshot) return plan;
  const { routeB, ...current } = plan;
  return {
    ...routeB.snapshot!,
    selectedRoute: plan.selectedRoute === "B" ? "A" : "B",
    routeB: { itinerary: current.itinerary, route: current.route, comparison: routeB.comparison, snapshot: current },
  };
}

export function bestRunMetaLabel(
  run: Pick<BestRunOption, "score" | "bestHour" | "reachable">,
): string {
  return `${Math.round(run.score)}/100 · ${formatHm(run.bestHour)}`;
}

export function bestRunRouteLabel(
  status: BestRunOption["routeStatus"],
): string | null {
  if (status === "route-a") return null;
  if (status === "route-b") return "on the alternative route";
  if (status === "out-of-reach") {
    return "not on today's route: out of reach in your window";
  }
  return "did not beat the chosen line";
}

export function briefingGroups(explanation: RouteExplanation): {
  title: "Highlights" | "Trade-offs" | "Data caveats";
  lines: string[];
}[] {
  return [
    { title: "Highlights" as const, lines: explanation.highlights },
    { title: "Trade-offs" as const, lines: explanation.tradeoffs },
    { title: "Data caveats" as const, lines: explanation.caveats },
  ].filter(({ lines }) => lines.length > 0);
}

const OBJECTIVE_KEYS = [
  "sun",
  "scenic",
  "firstTracks",
  "coverage",
  "cruise",
  "quiet",
] as const satisfies readonly (keyof PlannerObjectives)[];

function mergeObjectives(
  current: PlannerObjectives,
  patch: PlannerObjectives | undefined,
): PlannerObjectives {
  const merged = { ...current };
  if (!patch) return merged;
  for (const key of OBJECTIVE_KEYS) {
    const value = patch[key];
    if (value !== undefined) merged[key] = value;
  }
  return merged;
}

export function confidencePillProps(
  explanation: Pick<RouteExplanation, "confidenceLabel" | "confidenceTooltip">,
): { title: string; "aria-label": string } {
  return {
    title: explanation.confidenceTooltip,
    "aria-label": `Confidence: ${explanation.confidenceLabel}. ${explanation.confidenceTooltip}`,
  };
}

export function mergeRouteAlternativePatch(
  current: PlannerPreferences,
  patch: Partial<PlannerPreferences>,
): PlannerPreferences {
  return {
    ...current,
    ...patch,
    sport: current.sport,
    objectives: mergeObjectives(current.objectives, patch.objectives),
  } as PlannerPreferences;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="border-t border-border-light pt-3 first:border-t-0 first:pt-0">
      <h3 className="text-[11px] font-bold uppercase tracking-[0.6px] text-text-muted">{title}</h3>
      {children}
    </section>
  );
}

function RouteCard({ summary, selected }: { summary: RouteCardSummary; selected: boolean }) {
  const metrics = summary.dayMetrics;
  return (
    <article aria-label={`${summary.label}${selected ? ", selected" : ""}`} className={`rounded-[12px] border p-3 ${selected ? "border-teal bg-teal/5" : "border-border-light bg-warm-bg"}`}>
      <div className="flex items-start justify-between gap-3">
        <h4 className="text-[13px] font-bold text-navy">{summary.label}{selected && <span className="ml-2 text-xs text-teal">Selected</span>}</h4>
        <span className="text-[12px] font-bold tabular-nums text-gold">
          Finish {summary.finishTime}
        </span>
      </div>
      <p className="mt-1 text-[12px] leading-snug text-text-muted">
        {summary.sectorFocus}
      </p>
      <p className="mt-2 text-[11.5px] leading-snug text-text-muted">
        {summary.totals.skiKm.toFixed(1)} km · {Math.round(summary.totals.verticalM).toLocaleString("en-GB")} m · {summary.totals.runs} runs
      </p>
      <dl className="mt-3 space-y-1.5 text-xs text-navy">
        {metrics && <div className="flex justify-between gap-2"><dt>Time on trails</dt><dd className="font-semibold tabular-nums">{metrics.trailMinutes} min</dd></div>}
        <div className="flex justify-between gap-2"><dt>Lifts and estimated waits</dt><dd className="font-semibold tabular-nums">{summary.liftMinutes} min</dd></div>
        {metrics && <>
          <div className="flex justify-between gap-2"><dt>Walking</dt><dd className="font-semibold tabular-nums">{metrics.walkingMinutes} min</dd></div>
          <div className="flex justify-between gap-2"><dt>Hardest recorded grade</dt><dd className="text-right font-semibold capitalize">{metrics.hardestGrade ?? "Unavailable"}</dd></div>
          <div className="flex justify-between gap-2"><dt>Repeat laps</dt><dd className="font-semibold tabular-nums">{metrics.repeatRuns}</dd></div>
          {metrics.finishMarginMinutes != null && <div className="flex justify-between gap-2"><dt>Time before your deadline</dt><dd className="font-semibold tabular-nums">{metrics.finishMarginMinutes >= 0 ? `${metrics.finishMarginMinutes} min` : `${-metrics.finishMarginMinutes} min late`}</dd></div>}
        </>}
      </dl>
      {metrics?.unratedRuns && <p className="mt-2 text-xs text-amber-900">Some runs are unrated. A harder section may be unrecorded.</p>}
      {summary.bestRunNames.length > 0 ? (
        <p className="mt-2 text-[11.5px] leading-snug text-navy">
          <span className="font-semibold">Best:</span> {summary.bestRunNames.join(", ")}
        </p>
      ) : null}
    </article>
  );
}

export function RouteRecommendationPanel({
  plan,
  explanation,
  bestRunsToday,
  routeB,
  styleAlternatives,
  sport,
  selectedRoute = "A",
  pendingStyleKey,
  onSwapRouteB,
  onPlanStyle,
}: RouteRecommendationPanelProps) {
  const groups = briefingGroups(explanation);

  return (
    <div className="mb-4 rounded-[16px] border border-border-light bg-white p-4 md:p-5" data-export-ignore>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-serif text-[18px] font-bold leading-tight text-navy">Route briefing</h2>
          <p className="mt-1 text-[13px] leading-relaxed text-text-muted">{explanation.summary}</p>
        </div>
        <span
          {...confidencePillProps(explanation)}
          className="max-w-full rounded-full border border-border-light bg-warm-bg px-2.5 py-1 text-[10.5px] font-bold tracking-[0.5px] text-text-muted"
        >
          <span className="sr-only">Confidence: </span>
          {explanation.confidenceLabel}
        </span>
      </div>

      {plan && <RouteTrustSummary plan={plan} />}
      <div className="space-y-4">
        {routeB?.snapshot ? (
          <Section title="Two good ways to do today">
            <div className="mt-2 grid grid-cols-[repeat(auto-fit,minmax(min(100%,16rem),1fr))] gap-2">
              <RouteCard summary={routeB.comparison.a} selected={selectedRoute === "A"} />
              <RouteCard summary={routeB.comparison.b} selected={selectedRoute === "B"} />
            </div>
            <p className="mt-3 text-xs font-semibold text-navy">What changes with Route B</p>
            <ul className="mt-1 list-disc space-y-1 pl-4 text-[12.5px] leading-relaxed text-text-muted">{(routeB.comparison.differences ?? [routeB.comparison.keyTradeoff]).map(line => <li key={line}>{line}</li>)}</ul>
            <p className="mt-2 text-xs text-text-muted">Timings are estimates. The time before your deadline is spare time in this plan, not a guarantee against delays.</p>
            <button
              type="button"
              onClick={onSwapRouteB}
              className="mt-3 inline-flex min-h-[44px] items-center rounded-sm border border-navy/20 bg-white px-4 text-[13px] font-semibold text-navy transition-colors hover:border-gold/40"
            >
              {selectedRoute === "B" ? "Switch back to Route A" : routeBSwapLabel(sport)}
            </button>
          </Section>
        ) : null}

        {groups.length > 0 ? groups.map((group) => (
          <Section key={group.title} title={group.title}>
            <ul className="mt-2 space-y-1.5">
              {group.lines.slice(0, 5).map((line) => (
                <li key={line} className="text-[12.5px] leading-snug text-text-muted">
                  {line}
                </li>
              ))}
            </ul>
          </Section>
        )) : (
          <Section title="Highlights">
            <p className="mt-2 text-[12.5px] leading-snug text-text-muted">
              Balanced for the conditions, your settings and the route home.
            </p>
          </Section>
        )}

        <Section title="Best runs on these routes">
          {bestRunsToday.length > 0 ? (
            <ul className="mt-2 divide-y divide-border-light">
              {bestRunsToday.slice(0, 5).map((run) => (
                <li key={run.trailId} className="py-2 first:pt-0 last:pb-0">
                  <div className="flex items-start justify-between gap-3">
                    <p className="min-w-0 text-[13px] font-semibold leading-tight text-navy">
                      {run.name ?? "Unnamed run"}
                    </p>
                    <p className="shrink-0 text-[12px] font-bold tabular-nums text-gold">
                      {bestRunMetaLabel(run)}
                    </p>
                  </div>
                  <p className="mt-1 text-[12px] leading-snug text-text-muted">{run.reason}</p>
                  {bestRunRouteLabel(run.routeStatus) ? (
                    <p className="mt-1 text-[11.5px] leading-snug text-text-muted">
                      {bestRunRouteLabel(run.routeStatus)}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-[12.5px] leading-snug text-text-muted">
              No stand-out runs are reachable with this plan yet.
            </p>
          )}
        </Section>

        <Section title="Try another style">
          {styleAlternatives.length > 0 ? (
            <div className="mt-2 space-y-2">
              {styleAlternatives.map((alternative) => {
                const pending = pendingStyleKey === alternative.styleKey;
                return (
                  <button
                    key={alternative.styleKey}
                    type="button"
                    onClick={() => onPlanStyle(alternative)}
                    disabled={pending}
                    className="min-h-[44px] w-full border-t border-border-light py-2 text-left first:border-t-0 hover:text-navy disabled:cursor-wait disabled:opacity-70"
                  >
                    <span className="flex items-start justify-between gap-3">
                      <span className="min-w-0 text-[12px] leading-snug text-text-muted">
                        <span className="font-semibold text-navy">{alternative.label}:</span>{" "}
                        {alternative.blurb}
                      </span>
                      <span className="inline-flex shrink-0 items-center gap-1.5 text-[12px] font-bold text-gold">
                        {pending ? (
                          <>
                            <span
                              className="size-3 animate-spin rounded-full border-2 border-gold/30 border-t-gold"
                              aria-hidden="true"
                            />
                            <span role="status" aria-live="polite">Planning...</span>
                          </>
                        ) : STYLE_PLAN_ACTION_LABEL}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="mt-2 text-[12.5px] leading-snug text-text-muted">
              No other style is available with these settings.
            </p>
          )}
        </Section>
      </div>
    </div>
  );
}
